import {Activity, UserActivityCategoryEnum} from '@mobi-components/global-solution';
import {computed} from '@vue/composition-api';
import {formatBudget} from '@mobi-components/amex-bluesky-components';
import {useSolutionViews} from '../../solution-views/solution-views';
import {useSolutionFilters, VueWithModal} from '../../solution-views/summary-filters';
import {getFlightPrice} from '@common/main-flight-card/useFlightPrices';
import {bookActivity, isActivityBooked} from '@/store/state/helpers';

const getAirlineName = (flight: Record<'types', string[]>) => {
  if (!flight) return '';
  return flight.types[0];
};
export function useBookings(root: VueWithModal) {
  const {solution, isFlightAvailable, isFlightsOn, isStaysOn, flightsImg} = useSolutionViews(root);
  const isLoading = computed(() => !solution.value);
  const {travelersFilter} = useSolutionFilters(root);
  const totalPrice = computed(() =>
    formatBudget(solution.value?.price[0].amount ?? 0, solution.value?.price[0].currency, 'Points'),
  );
  const numberOfTravelers = computed(() => {
    const {adults, children} = travelersFilter;
    return adults + children;
  });

  const flights = computed(() => {
    return solution.value?.activities.filter((x) => x.category === UserActivityCategoryEnum.FLIGHT) || [];
  });

  const isFlightsBooked = computed(() => {
    return flights.value.every((x) => isActivityBooked(x.id));
  });

  const flightBookingGroup = computed(() => {
    // @ts-ignore
    const flightWithGroup = flights.value.find((x: Activity) => x.group_id);
    // @ts-ignore
    const group = (solution.value?.activity_groups || []).find((x) => x.id === flightWithGroup.group_id);
    return group?.source?.content;
  });

  const formattedGroupPrice = computed(() => {
    // @ts-ignore
    const groupPrices: Price[] = flightBookingGroup.value?.prices;
    const price = groupPrices?.length ? groupPrices[0] : null;

    return formatBudget(price?.amount, price?.currency, 'Points');
  });

  const flightGroup = computed(() => {
    return {
      images: flightsImg.value,
      url: isFlightsBooked.value ? '' : flightBookingGroup.value?.booking_url,
      name: 'Round trip',
      price: formattedGroupPrice.value,
      id: flightBookingGroup.value?.id,
      booked: isActivityBooked(flightBookingGroup.value?.id),
    };
  });

  const bookings = computed(() => {
    const allActivities = (solution?.value?.activities ?? [])
      // .filter(({price: {amount}}) => amount > 0)
      .map(({images, price, category, source = {}, id, types, spec = {}}: Activity) => {
        return {
          name: source?.content?.name,
          id,
          price: [UserActivityCategoryEnum.EXPERIENCE, UserActivityCategoryEnum.DINING].includes(category)
            ? ''
            : formatBudget(price?.amount, price?.currency, 'Points'),
          category,
          images: [images?.medium],
          url: source.content?.booking_url || '',
          guests: `${numberOfTravelers.value} People`,
          bookingSite: source.content?.website ?? 'External Site',
          source,
          types,
          webURL: source?.content?.url,
          curated: spec.curated,
          booked: isActivityBooked(id),
        };
      });

    return {
      flights: isFlightsOn.value
        ? flights.value.map((x) => {
            return {
              ...x,
              name: `${getAirlineName(x)}`,
              price: getFlightPrice(x),
              url: x.source?.content?.booking_url ?? '',
              booked: isActivityBooked(x.id),
              type: 'flight',
            };
          })
        : [],
      stays: isStaysOn.value
        ? allActivities
            ?.filter(({category}) => category === UserActivityCategoryEnum.ACCOMMODATION)
            .filter((_, i) => i === 0)
            .sort((x) => (x.url ? -1 : 1))
        : [],
      experiences: allActivities
        ?.filter(({category}) => category === UserActivityCategoryEnum.EXPERIENCE)
        .filter((x) => !x.curated)
        .sort((x) => (x.url ? -1 : 1)),
      dining: allActivities
        ?.filter(({category}) => category === UserActivityCategoryEnum.DINING)
        .filter((x) => !x.curated)
        .sort((x) => (x.url ? -1 : 1)),
    };
  });

  const onBookActivity = (id: string, status: boolean) => {
    const group = flightBookingGroup.value;
    const activity = (solution?.value?.activities ?? []).find((x) => x.id === id) || group;
    if (activity) {
      bookActivity(activity.id, status);
    }
  };

  return {
    totalPrice,
    bookings,
    numberOfTravelers,
    isLoading,
    onBookActivity,
    isFlightAvailable,
    isStaysOn,
    flightsImg,
    formattedGroupPrice,
    flightGroup,
    flights,
  };
}
